/**
 * Naked and hidden subsets (pairs, triples, quads).
 *
 * Both are `forEachConfinedSet` with the axes swapped:
 *   naked  -- n cells that between them allow only n digits, so those digits
 *             belong to those cells and leave the rest of the unit.
 *   hidden -- n digits that between them fit only n cells, so those cells
 *             belong to those digits and every other candidate leaves them.
 */

import { EMPTY, cellName, type Digit } from "../grid";
import { digitsOf, maskOf } from "../candidates";
import { unitName } from "../constraints";
import { forEachConfinedSet, type Confinable } from "./confined";
import type { Context, Elimination, Step, Technique, TechniqueId, Tier } from "./types";

const SIZE_WORD = ["", "", "Pair", "Triple", "Quad"] as const;

function digitList(mask: number): string {
  return digitsOf(mask).join(", ");
}

function findNakedSubset(ctx: Context, size: number, id: TechniqueId): Step | null {
  const { board, candidates, cs } = ctx;
  let result: Step | null = null;

  for (const constraint of cs.constraints) {
    const items: Confinable<number>[] = [];
    for (const cell of constraint.cells) {
      if (board[cell] === EMPTY) items.push({ ref: cell, slots: candidates[cell]! });
    }
    if (items.length <= size) continue; // nothing left to eliminate from

    forEachConfinedSet(items, size, ({ items: cells, slots }) => {
      const inSubset = new Set(cells);
      const eliminations: Elimination[] = [];
      for (const cell of constraint.cells) {
        if (board[cell] !== EMPTY || inSubset.has(cell)) continue;
        for (const digit of digitsOf(candidates[cell]! & slots)) {
          eliminations.push({ cell, digit });
        }
      }
      if (eliminations.length === 0) return false;

      result = {
        technique: id,
        pattern: cells,
        digits: digitsOf(slots),
        placements: [],
        eliminations,
        explanation:
          `Naked ${SIZE_WORD[size]}: ${cells.map(cellName).join(", ")} in ` +
          `${unitName(constraint)} hold only {${digitList(slots)}} between them, so those ` +
          `${size} digits fill those ${size} cells and can be removed from the rest of ` +
          `${unitName(constraint)}.`,
      };
      return true;
    });

    if (result) return result;
  }
  return null;
}

function findHiddenSubset(ctx: Context, size: number, id: TechniqueId): Step | null {
  const { board, candidates, cs } = ctx;
  let result: Step | null = null;

  for (const constraint of cs.constraints) {
    let placed = 0;
    for (const cell of constraint.cells) {
      if (board[cell] !== EMPTY) placed |= maskOf(board[cell]!);
    }

    // Slots are positions within the unit, so the mask is per-unit, not per-cell.
    const items: Confinable<Digit>[] = [];
    for (const digit of digitsOf(~placed & 0b111111111)) {
      let positions = 0;
      constraint.cells.forEach((cell, position) => {
        if (candidates[cell]! & maskOf(digit)) positions |= 1 << position;
      });
      if (positions !== 0) items.push({ ref: digit, slots: positions });
    }
    if (items.length <= size) continue;

    forEachConfinedSet(items, size, ({ items: subsetDigits, slots }) => {
      let keep = 0;
      for (const digit of subsetDigits) keep |= maskOf(digit);

      const cells: number[] = [];
      const eliminations: Elimination[] = [];
      constraint.cells.forEach((cell, position) => {
        if ((slots & (1 << position)) === 0) return;
        cells.push(cell);
        for (const digit of digitsOf(candidates[cell]! & ~keep)) {
          eliminations.push({ cell, digit });
        }
      });
      if (eliminations.length === 0) return false;

      result = {
        technique: id,
        pattern: cells,
        digits: subsetDigits,
        placements: [],
        eliminations,
        explanation:
          `Hidden ${SIZE_WORD[size]}: in ${unitName(constraint)}, {${digitList(keep)}} can ` +
          `only go in ${cells.map(cellName).join(", ")}. Those ${size} digits fill those ` +
          `${size} cells, so every other candidate can be removed from them.`,
      };
      return true;
    });

    if (result) return result;
  }
  return null;
}

function nakedSubset(size: 2 | 3 | 4, id: TechniqueId, tier: Tier, ser: number): Technique {
  return {
    id,
    name: `Naked ${SIZE_WORD[size]}`,
    tier,
    ser,
    find: (ctx) => findNakedSubset(ctx, size, id),
  };
}

function hiddenSubset(size: 2 | 3 | 4, id: TechniqueId, tier: Tier, ser: number): Technique {
  return {
    id,
    name: `Hidden ${SIZE_WORD[size]}`,
    tier,
    ser,
    find: (ctx) => findHiddenSubset(ctx, size, id),
  };
}

export const nakedPair = nakedSubset(2, "naked-pair", "medium", 3.0);
export const hiddenPair = hiddenSubset(2, "hidden-pair", "medium", 3.4);
export const nakedTriple = nakedSubset(3, "naked-triple", "hard", 3.6);
export const hiddenTriple = hiddenSubset(3, "hidden-triple", "hard", 4.0);
export const nakedQuad = nakedSubset(4, "naked-quad", "hard", 5.0);
export const hiddenQuad = hiddenSubset(4, "hidden-quad", "hard", 5.4);
